import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toTitleCase } from "./formatText";
import { formatCurrency } from "./formatNumber";

const AUTO_TARGET_LABEL_SELECTORS = {
  asinSubstituteRelated: ($) => $.advertising.substitutes,
  asinAccessoryRelated: ($) => $.advertising.complements,
  queryBroadRelMatches: ($) => $.advertising["loose match"],
  queryHighRelMatches: ($) => $.advertising["close match"],
};

const AUTO_TARGET_TYPES = [
  "asinSubstituteRelated",
  "asinAccessoryRelated",
  "queryBroadRelMatches",
  "queryHighRelMatches",
];

const AUTO_TARGET_TYPES_WITH_SIMILAR = [...AUTO_TARGET_TYPES, "similarProduct"];

const THEME_TARGET_LABEL_SELECTORS = {
  KEYWORDS_RELATED_TO_YOUR_PRODUCT_CATEGORY: ($) => $.advertising["category keywords"],
  KEYWORDS_RELATED_TO_YOUR_BRAND: ($) => $.advertising["brand keywords"],
  KEYWORDS_RELATED_TO_YOUR_LANDING_PAGES: ($) => $.advertising["landing page keywords"],
  KEYWORDS_RELATED_TO_PEER_BRANDS_PRODUCT_CATEGORY: ($) => $.advertising["peer brand keywords"],
  KEYWORDS_RELATED_TO_GIFTS: ($) => $.advertising["gift keywords"],
  KEYWORDS_RELATED_TO_PRIME_DAY: ($) => $.advertising["Prime Day keywords"],
};

const THEME_TARGET_TYPE_PREFIX = "KEYWORDS_RELATED_TO_";

export function useFormatTargetText() {
  const { t } = useTranslation("advertising");

  return useCallback(
    (targetText, options = {}) => {
      if (options?.name || options?.brand_name) {
        const ratingBetween = options.asin_review_rating_between;
        const ratingLt = options.asin_review_rating_less_than;
        const ratingGt = options.asin_review_rating_greater_than;
        const rating =
          ratingBetween ? "between"
          : ratingLt ? "lt"
          : ratingGt ? "gt"
          : "none";

        let ratingLow = "";
        let ratingHigh = "";
        let ratingValue = "";
        if (ratingBetween) {
          const parts = String(ratingBetween).split("-");
          ratingLow = parts[0] ?? "";
          ratingHigh = parts[1] ?? "";
        } else if (ratingLt) {
          ratingValue = String(ratingLt);
        } else if (ratingGt) {
          ratingValue = String(ratingGt);
        }

        const priceBetween = options.asin_price_between;
        const priceLt = options.asin_price_less_than;
        const priceGt = options.asin_price_greater_than;
        const price =
          priceBetween ? "between"
          : priceLt ? "lt"
          : priceGt ? "gt"
          : "none";

        let priceLow = "";
        let priceHigh = "";
        let priceValue = "";
        if (priceBetween) {
          const [low, high] = String(priceBetween).split("-");
          priceLow = formatCurrency(low, { marketPlace: options.marketPlace });
          priceHigh = formatCurrency(high, { marketPlace: options.marketPlace });
        } else if (priceLt) {
          priceValue = formatCurrency(priceLt, { marketPlace: options.marketPlace });
        } else if (priceGt) {
          priceValue = formatCurrency(priceGt, { marketPlace: options.marketPlace });
        }

        return t(($) => $.advertising.refinementDescription, {
          prime: options.asin_prime_eligible ? "yes" : "no",
          brand: options.brand_name ?? "",
          hasBrand: options.brand_name ? "yes" : "no",
          name: options.name ?? options.id ?? "",
          hasName: options.name || options.id ? "yes" : "no",
          rating,
          ratingLow,
          ratingHigh,
          ratingValue,
          price,
          priceLow,
          priceHigh,
          priceValue,
        });
      }

      if (typeof targetText !== "object") {
        return targetText;
      }

      if (typeof targetText?.value === "object" && targetText?.value?.length > 1) {
        return toTitleCase(
          `${targetText?.type} ${targetText?.value?.[0]?.type
            ?.replaceAll(/([A-Z]+)/g, " $1")
            ?.replaceAll(/([A-Z][a-z])/g, " $1")} ${
            targetText?.value?.[0]?.value ? `${targetText?.value?.[0]?.value}` : ""
          }, ${targetText?.value?.[1]?.value} day ${targetText?.value?.[1]?.type}`,
        ).replace("Asin", "ASIN");
      }

      if (targetText?.length > 1) {
        return targetText?.[0]?.value;
      }

      if (targetText?.length === 1) {
        if (AUTO_TARGET_TYPES.includes(targetText?.[0]?.type)) {
          return `${targetText?.[0]?.type}`;
        }
        return targetText?.[0]?.value;
      }

      if (AUTO_TARGET_TYPES_WITH_SIMILAR.includes(targetText?.type)) {
        const selector = AUTO_TARGET_LABEL_SELECTORS[targetText.type];
        return selector ? t(selector) : targetText.type;
      }

      if (typeof targetText?.type === "string" && targetText.type.startsWith(THEME_TARGET_TYPE_PREFIX)) {
        const selector = THEME_TARGET_LABEL_SELECTORS[targetText.type];
        // Amazon keeps adding keyword groups; an unlabelled one reads from its own name rather
        // than falling through to the placeholder.
        return selector ? t(selector) : targetText.type.replaceAll("_", " ").toLowerCase();
      }

      if (typeof targetText?.value === "object" && targetText?.value?.length === 1) {
        return targetText?.value?.[0]?.value;
      }

      return targetText?.value;
    },
    [t],
  );
}
